import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorEnum from 'model/ErrorEnum';
import { connectAuthentication } from 'services/api/connectAuth';
import ProfileInfoContext from 'storage/ProfileInfoContext';
import { renderAuthOutcome } from './renderAuthOutcome';
import { useConnectAuth } from '../components/Pages/Connect/hooks/useConnectAuth';

export default function withConnectAuthRequired(Component) {
  function ConnectAuth(props) {
    const { authError, getProfileInfo, isAuth, isFetching } = props;
    const { getConnectLoginData } = useConnectAuth();
    const [loadingProfile, setLoadingProfile] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      if (isFetching || loadingProfile || isAuth) return;

      const baseUrl = `${window.location.origin}/connect-error`;
      const logoutWithErrorPage = `${window?.env?.WOAD_CONNECT_LOGOUT_URL}${baseUrl}`;
      if (authError && authError.name !== ErrorEnum.UNAUTHORIZED) {
        window.location.href = logoutWithErrorPage;
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const state = params.get('state');
      const loginData = getConnectLoginData();

      if (state && loginData?.state !== state) {
        window.location.href = logoutWithErrorPage;
        return;
      }

      if (!code) {
        navigate('/connect-login');
        return;
      }

      const authenticate = async () => {
        setLoadingProfile(true);
        try {
          await connectAuthentication(code, loginData?.nonce);
          await getProfileInfo();
          if (code) {
            window.location.href = window.location.pathname;
          }
        } catch {
          window.location.href = logoutWithErrorPage;
        } finally {
          setLoadingProfile(false);
        }
      };

      authenticate();
    }, [authError, isAuth, isFetching, loadingProfile, getConnectLoginData, getProfileInfo, navigate]);

    return renderAuthOutcome(Component, props);
  }

  ConnectAuth.propTypes = {
    authError: PropTypes.shape({ name: PropTypes.string }),
    isAuth: PropTypes.bool.isRequired,
    isFetching: PropTypes.bool.isRequired,
    login: PropTypes.func,
    getProfileInfo: PropTypes.func.isRequired,
  };

  ConnectAuth.defaultProps = {
    authError: null,
    login: () => {},
  };

  return ProfileInfoContext.withContext(['authError', 'isAuth', 'isFetching', 'login', 'getProfileInfo'])(ConnectAuth);
}
